/**
 * When did THIS browsing session start?
 *
 * Used by the Inventory-beta welcome prompt, which must not appear in the same
 * session a dealer became eligible — they just finished importing or building a
 * batch, and interrupting that with a modal is how an invitation reads as an
 * ad. "Next session" is the sessionStorage lifetime: survives reloads and
 * in-app navigation, gone when the tab closes.
 *
 * Imports nothing, like `helpers/env.js`, so stores can read it freely.
 */
const SESSION_STARTED_KEY = 'cdp_session_started_at'

/**
 * ISO timestamp of the first call in this session, or `null` when sessionStorage
 * is unavailable (private windows, blocked site data).
 *
 * Callers must treat `null` as "can't tell" and fall through to SHOWING
 * whatever they were gating — never showing an invitation is a worse failure
 * than showing it a session early.
 */
export const sessionStartedAt = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_STARTED_KEY)

    if (existing) return existing

    const now = new Date().toISOString()
    sessionStorage.setItem(SESSION_STARTED_KEY, now)

    return now
  } catch {
    return null
  }
}

/**
 * Did this session start AFTER `isoTimestamp`?
 *
 * `true` when the timestamp is missing (nothing to wait for — e.g. every dealer
 * who was already eligible before this shipped) and when the session can't be
 * read at all.
 */
export const isLaterSessionThan = (isoTimestamp) => {
  if (!isoTimestamp) return true

  const started = sessionStartedAt()

  if (!started) return true

  return started > isoTimestamp
}
